import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import SEOHead from "@/components/SEOHead";
import EligibilityCheckForm from "@/components/EligibilityCheckForm";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Link } from "@/lib/router-compat";
import {
  Brain,
  Activity,
  Calendar,
  HelpCircle,
  CheckCircle2,
  Zap,
  Network,
} from "lucide-react";

export default function HowTmsTherapyAffectsTheBrainPage() {
  const publishedDate = "2024-07-02";
  const updatedDate = "2026-11-03";
  const heroImage =
    "https://neurowellnesstms.com/wp-content/uploads/2024/07/how-tms-therapy-affects-the-brain.jpg";

  const faqs = [
    {
      q: "How does TMS therapy affect the brain?",
      a: "TMS delivers focused magnetic pulses that depolarize neurons in the dorsolateral prefrontal cortex, increasing metabolic activity, neurotransmitter release (serotonin, dopamine, norepinephrine), and promoting long-term potentiation—strengthening synaptic connections damaged by chronic depression.",
    },
    {
      q: "Does TMS cause permanent changes to the brain?",
      a: "TMS promotes beneficial neuroplasticity—strengthening healthy neural pathways. These changes are therapeutic and long-lasting, with no evidence of harmful structural damage. Brain imaging shows normalized activity patterns after successful treatment.",
    },
    {
      q: "Which brain regions does TMS target?",
      a: "Standard TMS targets the dorsolateral prefrontal cortex (DLPFC), the executive control center for mood regulation. Advanced protocols like SAINT® use fMRI to personalize targeting to each patient's unique brain circuitry.",
    },
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="How TMS Therapy Affects the Brain: Neuroscience Explained | Neuro Wellness"
        description="Explore the neuroscience of how TMS therapy affects the brain—neuroplasticity, neurotransmitter release, long-term potentiation, and targeted cortical stimulation."
        canonical="/how-tms-therapy-affects-the-brain"
        keywords="how TMS affects the brain, TMS neuroplasticity, TMS neuroscience, dorsolateral prefrontal cortex stimulation, TMS brain changes"
        ogImage={heroImage}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "MedicalWebPage",
                "@id":
                  "https://neurowellnesstms.com/how-tms-therapy-affects-the-brain#webpage",
                name: "How TMS Therapy Affects the Brain",
                url: "https://neurowellnesstms.com/how-tms-therapy-affects-the-brain",
                inLanguage: "en-US",
                datePublished: publishedDate,
                dateModified: updatedDate,
                description:
                  "Detailed neuroscience explanation of TMS brain effects including neuroplasticity, neurotransmitter modulation, and long-term potentiation.",
                publisher: {
                  "@type": "MedicalOrganization",
                  name: "Neuro Wellness TMS Centers of America",
                  url: "https://neurowellnesstms.com",
                  telephone: "+1-954-827-9793",
                  address: {
                    "@type": "PostalAddress",
                    streetAddress: "9734 West Sample Road, 2nd Floor, Suite B",
                    addressLocality: "Coral Springs",
                    addressRegion: "FL",
                    postalCode: "33065",
                    addressCountry: "US",
                  },
                },
              },
              {
                "@type": "FAQPage",
                "@id":
                  "https://neurowellnesstms.com/how-tms-therapy-affects-the-brain#faq",
                mainEntity: faqs.map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
            ],
          }),
        }}
      />

      <Navbar />

      <main className="pt-16 sm:pt-20">
        {/* HERO */}
        <section className="relative overflow-hidden bg-primary-deep text-primary-foreground py-12 sm:py-16 lg:py-20">
          <div className="absolute inset-0 bg-gradient-to-br from-primary-deep via-primary-deep/95 to-slate-900" />
          <div className="container-px relative mx-auto max-w-6xl">
            <div className="grid items-center gap-8 lg:grid-cols-12 lg:gap-12">
              <div className="order-2 lg:order-1 lg:col-span-7">
                <div className="flex flex-wrap items-center gap-3 mb-4">
                  <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/15 px-3.5 py-1 text-xs font-semibold text-accent backdrop-blur-sm">
                    <Network className="h-4 w-4" />
                    Neuroscience Deep Dive
                  </span>
                  <span className="text-xs text-primary-foreground/75 flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    Medically Reviewed · Updated July 2024
                  </span>
                </div>

                <h1 className="font-headings text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight text-white">
                  How TMS Therapy{" "}
                  <span className="text-accent">Affects the Brain</span>
                </h1>

                <p className="mt-5 text-base sm:text-lg text-primary-foreground/90 leading-relaxed max-w-2xl">
                  The neuroscience behind magnetic stimulation—neuroplasticity,
                  neurotransmitter release, and the restoration of healthy brain
                  circuits.
                </p>
              </div>

              <div className="order-1 lg:order-2 lg:col-span-5">
                <div className="rounded-2xl border border-white/15 bg-white/5 p-6 shadow-2xl backdrop-blur-md">
                  <div className="text-center mb-4">
                    <span className="inline-block rounded-full bg-accent/20 px-3 py-1 text-xs font-semibold text-accent">
                      Neuroscience Consult
                    </span>
                    <h3 className="text-lg font-bold text-white mt-1">
                      Check TMS Eligibility
                    </h3>
                  </div>
                  <EligibilityCheckForm />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* BREADCRUMBS & ARTICLE */}
        <section className="py-10 bg-background">
          <div className="container-px mx-auto max-w-4xl">
            <Breadcrumbs
              crumbs={[
                { name: "Home", href: "/" },
                { name: "Articles", href: "/articles" },
                {
                  name: "How TMS Affects the Brain",
                  href: "/how-tms-therapy-affects-the-brain",
                },
              ]}
            />

            <article className="prose prose-slate max-w-none dark:prose-invert prose-headings:font-headings prose-headings:text-foreground prose-p:text-foreground/90 prose-p:leading-relaxed mt-6">
              <p className="text-lg leading-relaxed font-medium">
                To understand why TMS therapy is so effective, we must examine
                what happens at the cellular and network level of the brain when
                magnetic pulses are applied. The answer lies in{" "}
                <strong>neuroplasticity</strong>—the brain's remarkable ability
                to rewire itself.
              </p>

              <h2>The Depressed Brain: Underactive Circuitry</h2>
              <p>
                Functional brain imaging (fMRI and PET scans) consistently shows
                that individuals with depression have reduced metabolic activity
                in the <strong>dorsolateral prefrontal cortex (DLPFC)</strong>.
                This region governs executive function, motivation, and
                emotional regulation. When it's underactive, the brain's
                mood-stabilizing networks falter.
              </p>

              <h2>Magnetic Pulses & Neuronal Depolarization</h2>
              <p>
                TMS generates a rapidly changing magnetic field that passes
                through the skull harmlessly. When this field reaches cortical
                neurons, it induces electrical currents that{" "}
                <strong>depolarize</strong> the cell membranes, triggering
                action potentials—essentially "waking up" dormant neurons.
              </p>

              <h2>Neurotransmitter Release</h2>
              <p>
                Repeated stimulation increases the release of key
                neurotransmitters:{" "}
                <strong>serotonin, norepinephrine, and dopamine</strong>. This
                mirrors the goal of antidepressant medications, but achieves it
                through direct neural activation rather than chemical reuptake
                inhibition—without systemic side effects.
              </p>

              <h2>Long-Term Potentiation (LTP)</h2>
              <p>
                The most profound effect of TMS is{" "}
                <strong>long-term potentiation</strong>—a persistent
                strengthening of synapses based on recent activity patterns.
                Repeated TMS sessions literally rebuild the synaptic connections
                that chronic stress and depression have eroded, creating
                durable, lasting change.
              </p>

              <h2>Network-Level Effects</h2>
              <p>
                TMS doesn't just affect the DLPFC in isolation. Through{" "}
                <strong>functional connectivity</strong>, stimulating this hub
                normalizes activity across the broader default mode network and
                limbic system, restoring balanced emotional processing. Advanced
                protocols like{" "}
                <Link
                  to="/saint"
                  className="text-primary hover:underline font-semibold"
                >
                  SAINT®
                </Link>{" "}
                use fMRI to personalize the target to each patient's unique
                circuitry.
              </p>
            </article>

            {/* FAQ SECTION */}
            <div className="my-12 border-t border-border pt-8">
              <h2 className="text-2xl font-bold text-foreground flex items-center gap-2 mb-6">
                <HelpCircle className="h-6 w-6 text-primary" />
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {faqs.map((faq, idx) => (
                  <div
                    key={idx}
                    className="rounded-xl border border-border bg-card p-5"
                  >
                    <h3 className="font-semibold text-foreground text-base sm:text-lg">
                      {faq.q}
                    </h3>
                    <p className="mt-2 text-sm text-foreground/80 leading-relaxed">
                      {faq.a}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* RELATED RESOURCES */}
            <div className="my-10 rounded-xl bg-muted/40 p-6 border border-border">
              <h3 className="text-lg font-bold text-foreground mb-3">
                Related Neuroscience Guides
              </h3>
              <ul className="grid sm:grid-cols-2 gap-2 text-sm text-primary font-medium">
                <li>
                  <Link to="/what-is-tms-therapy" className="hover:underline">
                    → What Is TMS Therapy?
                  </Link>
                </li>
                <li>
                  <Link
                    to="/how-tms-therapy-works-coral-springs-fl"
                    className="hover:underline"
                  >
                    → How TMS Therapy Works
                  </Link>
                </li>
                <li>
                  <Link
                    to="/how-does-bipolar-disorder-affect-the-brain"
                    className="hover:underline"
                  >
                    → How Bipolar Affects the Brain
                  </Link>
                </li>
                <li>
                  <Link to="/saint" className="hover:underline">
                    → SAINT® fMRI-Guided TMS
                  </Link>
                </li>
              </ul>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
